import {Action, State, StateContext} from '@ngxs/store';
import {ViewType} from '@shared/enums/view-type.enum';
import {Injectable} from '@angular/core';
import {UserService} from '@services/user/user.service';
import {Observable, of} from 'rxjs';
import {ICompany, IDeviceManagement, ITimezone, IUser} from '@interfaces/user';
import {catchError, mergeMap, share, switchMap, tap} from 'rxjs/operators';
import {UserStateModel} from './user-state.model';
import {
    AddUser,
    ChangeAutoDeletePeriod, DeleteUser, GetCompanyData, GetCompanyUsers,
    GetDeviceManagementSettings, GetFeaturesConfig,
    GetUser, SetAutoRemoteUpdateStatus,
    ToggleAutoDeleteStatus,
    ToggleAutoenrollStatus,
    ToggleDeviceManagementStatus,
    ToggleDeviceView, ToggleMarketplaceMode,
    ToggleStatisticStatus,
    ToggleTwoFA,
    UpdateIpWhiteList, UpdateMarketplaceUserData,
    UpdatePassword,
    UpdateTimezone, UpdateUser,
    UpdateUserData
} from './user.actions';
import {GetUserForPayment, PaymentState} from '@store/payment';
import * as moment from 'moment';

@State<UserStateModel>({
    name: 'userState',
    defaults: {
        user: null,
        company: null,
        deviceManagement: {
            status: false,
        },
        companyUsers: [],
        viewType: ViewType.list,
        homeFolderIds: {
            homeMedia: null
        },
        timezone: null,
        features: {}
    },
    children: [PaymentState]
})
@Injectable()
export class UserState {
    constructor(
        private userService: UserService,
    ) {
    }

    // Actions
    @Action(GetUserForPayment)
    @Action(GetUser)
    getUser(ctx: StateContext<UserStateModel>): Observable<IUser> {
        return this.userService.getUser().pipe(
            tap((user) => {
                const userRole = user.role;
                localStorage.setItem('DwallRole', userRole);
                const state = ctx.getState();
                ctx.setState({
                    ...state, user: {...user, role: userRole}
                });
            })
        );
    }

    @Action(GetCompanyData, {cancelUncompleted: true})
    getCompanyData(ctx: StateContext<UserStateModel>): Observable<ICompany> {
        return this.userService.getCompanyData()
            .pipe(
                tap((company) => {
                    const userFullTimezone = this.userService.getTimezones().find(tz => {
                        if (company.timezone) return tz.name === company.timezone
                        else return moment.tz.guess() === tz.name; // default timezone from browser
                    }) as ITimezone;

                    // set default timezone to BE
                    if (!company.timezone) ctx.dispatch(new UpdateTimezone(userFullTimezone));

                    const state = ctx.getState();
                    ctx.setState({
                        ...state, company: {...company}, timezone: userFullTimezone,
                        homeFolderIds: {...state.homeFolderIds, homeMedia: company.home_folder_id}
                    });
                })
            );
    }

    @Action(GetFeaturesConfig)
    getFeaturesConfig(ctx: StateContext<UserStateModel>): Observable<Record<string, boolean>> {
        return this.userService.getFeaturesConfig()
            .pipe(
                catchError(() => of({})),
                tap((features) => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, features: features || {}
                    });
                })
            );
    }

    @Action(UpdateUserData, {cancelUncompleted: true})
    updateUserData(ctx: StateContext<UserStateModel>, {profile}: UpdateUserData): Observable<any> {
        return this.userService.updateUserData(profile)
            .pipe(
                tap((user) => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, user: {...state.user, ...profile} as IUser
                    });
                }),

                // TODO it could be skipped if back end will working properly
                mergeMap(() => ctx.dispatch(new GetUser()))
            );
    }

    @Action(ToggleTwoFA, {cancelUncompleted: true})
    toggleTwoFA(ctx: StateContext<UserStateModel>, {event}: ToggleTwoFA): Observable<any> {
        return this.userService.toggleTwoFA(event)
            .pipe(
                tap((res) => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, company: {...state.company, two_fa: event ? 1 : 0} as ICompany
                    });
                }),

                // TODO it could be skipped if back end were worked properly
                mergeMap(() => ctx.dispatch(new GetUser()))
            );
    }

    @Action(ToggleMarketplaceMode, {cancelUncompleted: true})
    toggleMarketplaceMode(ctx: StateContext<UserStateModel>, {status}: ToggleMarketplaceMode): Observable<any> {
        const stream$ = this.userService.setMarketplaceMode(status)
            .pipe(
                tap(() => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, company: {...state.company, marketplace_mode: status} as ICompany
                    });
                }),

                switchMap(() => ctx.dispatch(new GetCompanyData())),
                share(),
            );

        stream$.subscribe({
            error: err => {
                const state = ctx.getState();
                ctx.setState({
                    ...state, company: {...state.company, marketplace_mode: !status} as ICompany
                });
            }
        });
        return stream$;
    }

    @Action(UpdateMarketplaceUserData, {cancelUncompleted: true})
    updateMarketplaceUserData(ctx: StateContext<UserStateModel>, {data}: UpdateMarketplaceUserData): Observable<any> {
        return this.userService.updateBillingData(data)
            .pipe(
                tap((res) => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, company: {...state.company} as ICompany
                    });
                })
            );
    }

    @Action(UpdateIpWhiteList, {cancelUncompleted: true})
    updateIpWhiteList(ctx: StateContext<UserStateModel>, {ipList}: UpdateIpWhiteList): Observable<any> {
        return this.userService.updateIpWhiteList(ipList)
            .pipe(
                tap((res) => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, company: {...state.company, ip_white_list: ipList} as ICompany
                    });
                }),

                // TODO it could be skipped if back end were worked properly
                mergeMap(() => ctx.dispatch(new GetCompanyData()))
            );
    }

    @Action(UpdatePassword, {cancelUncompleted: true})
    updatePassword(ctx: StateContext<UserStateModel>, {pass}: UpdatePassword): Observable<any> {
        return this.userService.updatePass(pass)
            .pipe(
                tap(() => {
                    // do some stuff with response
                })
            );
    }

    @Action(UpdateTimezone, {cancelUncompleted: true})
    updateTimezone(ctx: StateContext<UserStateModel>, {timezone}: UpdateTimezone): Observable<any> {
        return this.userService.updateTimezone(timezone)
            .pipe(
                tap(() => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, timezone
                    });
                })
            );
    }


    // Device management Actions
    @Action(GetDeviceManagementSettings, {cancelUncompleted: true})
    getDeviceManagementSettings(ctx: StateContext<UserStateModel>): Observable<any> {
        return this.userService.getDeviceManagementSettings()
            .pipe(
                tap((data: { status: boolean, corporateId: string }) => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, deviceManagement: {...state.deviceManagement, ...data} as IDeviceManagement
                    });
                })
            );
    }

    @Action(ToggleDeviceManagementStatus, {cancelUncompleted: true})
    toggleDeviceManagementStatus(ctx: StateContext<UserStateModel>, {status}: ToggleDeviceManagementStatus): Observable<any> {
        return this.userService.toggleDeviceManagementStatus(status)
            .pipe(
                tap(() => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, deviceManagement: {...state.deviceManagement, status}
                    });
                }),

                // TODO it could be skipped if back end were worked properly
                mergeMap(() => ctx.dispatch(new GetDeviceManagementSettings()))
            );
    }

    @Action(ToggleAutoenrollStatus, {cancelUncompleted: true})
    toggleAutonenrollStatus(ctx: StateContext<UserStateModel>, {status}: ToggleAutoenrollStatus): Observable<any> {
        return this.userService.toggleAutoenroll(status)
            .pipe(
                tap(() => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, company: {...state.company, auto_enroll_enabled: status ? 1 : 0} as ICompany
                    });
                }),

                // TODO it could be skipped if back end were worked properly
                mergeMap(() => ctx.dispatch(new GetCompanyData()))
            );
    }

    @Action(ToggleAutoDeleteStatus, {cancelUncompleted: true})
    toggleAutoDeleteStatus(ctx: StateContext<UserStateModel>, {status}: ToggleAutoDeleteStatus): Observable<any> {
        return this.userService.toggleAutoDelete(status)
            .pipe(
                tap(() => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, company: {...state.company, auto_delete_setting: status ? 1 : 0} as ICompany
                    });
                }),

                // TODO it could be skipped if back end were worked properly
                mergeMap(() => ctx.dispatch(new GetUser()))
            );
    }

    @Action(ChangeAutoDeletePeriod, {cancelUncompleted: true})
    changeAutoDeletePeriod(ctx: StateContext<UserStateModel>, {period}: ChangeAutoDeletePeriod): Observable<any> {
        return this.userService.changeAutoDeletePeriod(period.size)
            .pipe(
                tap(() => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, company: {...state.company, auto_delete_period: period.size} as ICompany
                    });
                }),

                // TODO it could be skipped if back end were worked properly
                mergeMap(() => ctx.dispatch(new GetUser()))
            );
    }

    @Action(ToggleStatisticStatus, {cancelUncompleted: true})
    toggleStatisticStatus(ctx: StateContext<UserStateModel>, {status}: ToggleStatisticStatus): Observable<any> {
        return this.userService.toggleStatistic(status)
            .pipe(
                tap(() => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, company: {...state.company, statistic_setting: status ? 1 : 0} as ICompany
                    });
                }),
                catchError(() => of('')),

                // TODO it could be skipped if back end were worked properly
                mergeMap(() => ctx.dispatch([new GetUser(), new GetCompanyData()]))
            );
    }

    @Action(ToggleDeviceView)
    toggleDeviceView(ctx: StateContext<UserStateModel>, {type}: ToggleDeviceView): void {
        const state = ctx.getState();
        ctx.setState({
            ...state, viewType: type
        });
    }

    @Action(GetCompanyUsers)
    getCompanyUsers(ctx: StateContext<UserStateModel>): Observable<IUser[]> {
        return this.userService.getCompanyUsers()
            .pipe(
                tap((users) => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, companyUsers: users
                    });
                })
            );
    }

    @Action(AddUser)
    addUser(ctx: StateContext<UserStateModel>, {user}: AddUser): Observable<any> {
        return this.userService.addUserToCompany(user)
            .pipe(
                tap((res) => {
                    if (!res) return;
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, companyUsers: [res, ...state.companyUsers]
                    });
                })
            );
    }

    @Action(DeleteUser)
    deleteUser(ctx: StateContext<UserStateModel>, {id}: DeleteUser): Observable<any> {
        return this.userService.deleteUser(id)
            .pipe(
                tap(() => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, companyUsers: state.companyUsers.filter((user) => user.id !== id)
                    });
                })
            );
    }

    @Action(UpdateUser)
    updateUser(ctx: StateContext<UserStateModel>, {data}: UpdateUser): Observable<any> {
        return this.userService.updateUser(data)
            .pipe(
                mergeMap(() => ctx.dispatch(new GetCompanyUsers()))
            );
    }

    @Action(SetAutoRemoteUpdateStatus)
    setAutoRemoteUpdateStatus(ctx: StateContext<UserStateModel>, {status}: SetAutoRemoteUpdateStatus): Observable<any> {
        return this.userService.setAutoRemoteUpdateStatus(status)
            .pipe(
                tap(data => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state, company: {...state.company, auto_remote_update_enabled: data.status} as ICompany
                    })
                })
            )
    }
}
